import axios from 'axios';
import _ from 'lodash';
import get from 'lodash/get';
import moment from 'moment';
import { action, computed, observable, toJS } from 'mobx';
import numeral from 'numeral';
import 'numeral/locales/it';
import CalculationFormData from './CalculationFormStore';

numeral.locale('it');

/* Define constant tabs */
const tabs = [
    { id: 'overview', name: 'Totaal overzicht' },
    { id: 'loanpart-1', name: 'Leningdeel 1' },
    { id: 'loanpart-2', name: 'Leningdeel 2' },
    { id: 'loanpart-3', name: 'Leningdeel 3' },
];

const timeOutTime = 1000;

class CalculationOutputStore {
  constructor() {
    this.getTotalRiskClassReductionMonths = this.getTotalRiskClassReductionMonths.bind(this);
    this.getTotalData = this.getTotalData.bind(this);
    this.getTotalMonthsData = this.getTotalMonthsData.bind(this);
    this.calculationResponseByTotal = this.calculationResponseByTotal.bind(this);
    this.allMonths = this.allMonths.bind(this);
    this.allRiskClassReductionMonths = this.allRiskClassReductionMonths.bind(this);
    this.calculateLoanparts = this.calculateLoanparts.bind(this);
    this.downloadPdf = this.downloadPdf.bind(this);
    this.validateLoanparts = this.validateLoanparts.bind(this);
    this.mitigateDifferenceMortgageLoanparts = this.mitigateDifferenceMortgageLoanparts.bind(this);
    this.calculateLoanpartsWithoutForceAutoRiskClassReduction = this.calculateLoanpartsWithoutForceAutoRiskClassReduction.bind(this);
    this.setMortgageLoanpartsDifference = this.setMortgageLoanpartsDifference.bind(this);
    this.removeLoanpart = this.removeLoanpart.bind(this);
    this.clearErrorMessages = this.clearErrorMessages.bind(this);
  }

  @observable accessor calculationResponse;
  /* Default tab is overview */
  @observable accessor activeLoanpartOverviewTabId = 'overview';

  @observable accessor calculationResponseLoading;

  @observable accessor allMonthsData;
  @observable accessor allRiskClassReductionMonthData;
  @observable accessor calculationResponseByTotalData;

  @observable accessor textDownloadReport = '';

  @observable accessor shallowFormDataCopy;

  @observable accessor mortgageLoanpartsDifference = 0;

  @observable accessor responseError = '';

  /* Set totalInterestPaymentWithoutForceAutoRiskClassReduction */
  @observable accessor totalInterestPaymentWithoutForceAutoRiskClassReduction;

  async calculateLoanparts() {
    this.calculationResponseLoading = true;
    let res;
    const self = this;

    /* Check for empty loanparts */
    const shallowCopyCalculationData = toJS({
      ...CalculationFormData.calculationData,
      selectedLabelName: CalculationFormData.selectedLabelName,
    });
    this.shallowFormDataCopy = shallowCopyCalculationData;

    const filledLoanparts = shallowCopyCalculationData.loanparts.filter(loanpart => loanpart.gross > 0);
    const validCalculationFormData = this.validateLoanparts(filledLoanparts);
    this.shallowFormDataCopy.loanparts = filledLoanparts;

    if (validCalculationFormData) {
      CalculationFormData.loanpartError = '';

      res = await axios.post(
        '/calculate/loanparts',
        this.shallowFormDataCopy,
      )
        .then((data) => {
          self.clearErrorMessages();
          self.calculationResponse = data;
          self.allMonths();
          self.allRiskClassReductionMonths();
          self.calculationResponseByTotal();

          return data;
        })
        .catch((error) => {
          if (get(error, 'response.status', null) === 422) {
            CalculationFormData.errorFields = _.values(error.response.data);
            CalculationFormData.errorFieldKeys = _.keys(error.response.data);
            CalculationFormData.responseErrorFields = 'De volgende velden zijn foutief ingevuld:';
          } else {
            CalculationFormData.responseError = `Er is iets fout gegaan met de berekening.
         Probeer het op een later tijdstip.`;
          }

          return error;
        });
    }
    this.calculationResponseLoading = false;

    return res;
  }

  /* Calculate loanparts for the total.total.interestPayment only. Might create a new endpoint for this one. */
  async calculateLoanpartsWithoutForceAutoRiskClassReduction() {
    const copyOfShallowFormDataCopy = { ...this.shallowFormDataCopy };
    copyOfShallowFormDataCopy.forceAutoRiskClassReduction = false;

    const res = await axios.post(
      '/calculate/loanparts',
      copyOfShallowFormDataCopy,
    )
    .then((response) => {
      this.totalInterestPaymentWithoutForceAutoRiskClassReduction = response.data.total.total.interestPayment;
    });

    return res;
  }

  /* Helper method for calculateLoanparts */
  @action validateLoanparts(filledLoanparts) {
    const { calculationData } = CalculationFormData;
    const emptyCalculationData = _.some(calculationData, value => value === '');

    /* If some form fields are empty, show responseError. */
    if (emptyCalculationData) {
      CalculationFormData.responseError = 'Controleer of alle velden goed zijn ingevuld.';
    }

    /* If some loanparts are too low, show responseError. */
    let loanpartTooLow;
    if (filledLoanparts) {
      loanpartTooLow = filledLoanparts.length > 0 && filledLoanparts.some(loanpart => loanpart.gross < 1000);
      if (loanpartTooLow) {
        CalculationFormData.responseError = 'De hoogte van een leningdeel mag niet lager zijn dan €1.000,-';
      }
    }

    this.setMortgageLoanpartsDifference();

    return !emptyCalculationData && this.mortgageLoanpartsDifference === 0 && !loanpartTooLow;
  }

  @action setMortgageLoanpartsDifference() {
    const { mortgageForm, calculationData } = CalculationFormData;
    const sumLoanparts = _.sum(mortgageForm.loanparts.map(loanpart => Number(loanpart)));
    this.mortgageLoanpartsDifference = calculationData.mortgage - sumLoanparts;
  }

  @action mitigateDifferenceMortgageLoanparts(difference, loanpartIndex) {
    const gross = CalculationFormData.mortgageForm.loanparts[loanpartIndex];

    /* If difference is positive */
    if (Number(difference) > 0) {
      CalculationFormData.mortgageForm.loanparts[loanpartIndex] = Number(gross) + Number(difference);

    /* If difference is negative compare to loanpart */
    } else if (Number(difference < 0)) {
      if (Number(gross) <= Math.abs(Number(difference))) {
        CalculationFormData.mortgageForm.loanparts.splice(loanpartIndex, 1);
      } else if (Number(gross) >= Math.abs(Number(difference))) {
        CalculationFormData.mortgageForm.loanparts[loanpartIndex] = Number(gross) + Number(difference);
      }
    }

    this.validateLoanparts();
  }

  @action removeLoanpart(loanpartIndex) {
    CalculationFormData.mortgageForm.loanparts.splice(loanpartIndex, 1);
    this.validateLoanparts();
  }

  @computed get allTabs() {
    return tabs;
  }

  /* Helper method for calculateLoanparts */
  @action clearErrorMessages() {
    CalculationFormData.errorFields = [];
    CalculationFormData.errorFieldKeys = [];
    CalculationFormData.responseError = '';
    CalculationFormData.responseErrorFields = '';
  }

  @action calculationResponseByTotal() {
    let newTotal;

    const totalData = this.getTotalData(this.activeLoanpartOverviewTabId);

    if (totalData) {
      newTotal = { ...totalData };

      newTotal.interestPayment = numeral(totalData.interestPayment).format('$ 0,00.00');
      newTotal.repayment = numeral(totalData.repayment).format('$ 0,00.00');
      newTotal.redemption = numeral(totalData.interestPayment + totalData.repayment).format('$ 0,00.00');
    }

    this.calculationResponseByTotalData = newTotal;
  }

  /**
   * Helper method for calculationResponseByTotal
   * @param activeLoanpartOverviewTabId
   * @returns monts
   */
  @action getTotalData(activeLoanpartOverviewTabId) {
    let total;
    if (_.has(this.calculationResponse, 'data')) {
      const loanparts = this.calculationResponse.data.loanParts;
      switch (activeLoanpartOverviewTabId) {
      case 'overview':
        total = this.calculationResponse.data.total.total;
        break;
      case 'loanpart-1':
        total = loanparts[0].total;
        break;
      case 'loanpart-2':
        total = loanparts[1].total;
        break;
      case 'loanpart-3':
        total = loanparts[2].total;
        break;
      default:
        total = 'overview';
      }
    }
    return total;
  }

  @action allMonths() {
    const totalMonths = this.getTotalMonthsData(this.activeLoanpartOverviewTabId);
    let currentMonth = 1;
    this.allMonthsData = totalMonths.filter((month) => {
      let filteredMonth;
      if (month.month === currentMonth) {
        filteredMonth = month;
        currentMonth += 12;
      }

      return filteredMonth;
    })
      .map((month) => {
        const newMonth = { ...month };
        newMonth.year = moment(month.date, 'YYYY/MM/DD').format('YYYY');
        return newMonth;
      })
      .map((month, index) => {
        const newMonth = this.addNumberFormattingByMonth(this.addMonthlyDebt(month));

        if (index === 0) {
          newMonth.year = `${month.year} (eerste maand)`;
        }

        if (index === (totalMonths.length / 12)) {
          newMonth.year = `${month.year} (laatste maand)`;
        }

        return newMonth;
      });
  }

  @action allRiskClassReductionMonths() {
    const monthData = this.getTotalRiskClassReductionMonths(this.activeLoanpartOverviewTabId).map((month) => {
      const newMonth = { ...month };
      newMonth.year = moment(month.date, 'YYYY/MM/DD').format('YYYY');
      return newMonth;
    });

    this.allRiskClassReductionMonthData = monthData;
  }

  /**
   * Helper method for allMonths
   * @param activeLoanpartOverviewTabId
   * @returns monts
   */
  @action getTotalMonthsData(activeLoanpartOverviewTabId) {
    let months;
    if (_.has(this.calculationResponse, 'data')) {
      const loanparts = this.calculationResponse.data.loanParts;

      switch (activeLoanpartOverviewTabId) {
      case 'overview':
        months = this.calculationResponse.data.total.months;
        break;
      case 'loanpart-1':
        months = loanparts[0].months;
        break;
      case 'loanpart-2':
        months = loanparts[1].months;
        break;
      case 'loanpart-3':
        months = loanparts[2].months;
        break;
      default:
        months = 'overview';
      }
    }
    return months;
  }

  /**
   * Helper method for getTotalRiskClassReductionMonths
   * @param activeLoanPartOverviewTabId
   * @returns months
   */
  @action getTotalRiskClassReductionMonths(activeLoanPartOverviewTabId) {
    let months;
    if (_.has(this.calculationResponse, 'data')) {
      const loanparts = this.calculationResponse.data.loanParts;

      switch (activeLoanPartOverviewTabId) {
      case 'overview':
        months = this.calculationResponse.data.total.total.riskClassReduction;
        break;
      case 'loanpart-1':
        months = loanparts[0].total.riskClassReduction;
        break;
      case 'loanpart-2':
        months = loanparts[1].total.riskClassReduction;
        break;
      case 'loanpart-3':
        months = loanparts[2].total.riskClassReduction;
        break;
      default:
        months = 'overview';
      }
    }
    return months;
  }

  @action addMonthlyDebt(month) {
    const newMonth = { ...month };
    newMonth.monthlyPayment = Math.floor((newMonth.gross + newMonth.repayment) * 100) / 100;
    return newMonth;
  }

  /**
   *
   * @param month
   * @returns month with number formatting. Helper method for getRiskClassReductionDates and
   * allMonths.
   */
  @action addNumberFormattingByMonth(month) {
    const newMonth = { ...month };
    newMonth.interest = numeral(month.interest / 100).format('%0.00');
    newMonth.remainingDebt = numeral(month.remainingDebt).format('$ 0,00.00');
    newMonth.gross = numeral(month.gross).format('$ 0,00.00');
    newMonth.repayment = numeral(month.repayment).format('$ 0,00.00');
    newMonth.monthlyPayment = numeral(month.monthlyPayment).format('$ 0,00.00');
    newMonth.interestDiscount = numeral(month.interestDiscount / 100).format('%0.00');
    newMonth.monthlyPaymentDiscount = numeral(month.monthlyPaymentDiscount).format('$ 0,00.00');
    return newMonth;
  }

  @action setActiveLoanpartOverviewTab(activeTab) {
    this.activeLoanpartOverviewTabId = activeTab;
  }

  /**
   *
   * @returns totals of all loanparts for loanpartSummary
   */
  @computed get loanpartsTotals() {
    let loanpartTotals;

    if (_.has(this.calculationResponse, 'data')) {
      loanpartTotals = this.calculationResponse.data.loanParts
        .map(loanpart => loanpart.total);
    }

    return loanpartTotals;
  }

  @action
  async downloadPdf() {
    const formData = this.shallowFormDataCopy;

    const formattedFormdata = {
      ...formData,
      labelId: Number(formData.labelId),
      houseValue: Number(formData.houseValue),
      mortgage: Number(formData.mortgage),
      labelName: formData.selectedLabelName,
      forceAutoRiskClassReduction: formData.forceAutoRiskClassReduction,
    };

    const postData = JSON.stringify(formattedFormdata);

    await axios.get(`/download/pdf?formdata=${postData}`)
      .then((response) => {
        this.responseError = '';
        window.location.href = `/download/serve-pdf/${response.data.token}`;
        setTimeout(() => {
          this.setTextDownloadReport('');
        }, timeOutTime);
      })
      .catch(() => {
        this.responseError = 'Er is iets mis gegaan tijdens het downloaden van het rapport. ' +
          'Probeer het later nog een keer.';
        this.setTextDownloadReport('');
      });
  }

  @action setTextDownloadReport(message) {
    this.textDownloadReport = message;
  }
}

const CalculationOutputData = new CalculationOutputStore();

export default CalculationOutputData;
